import { Component } from 'react'
import PropTypes from 'prop-types'
import { useRouteError, Link } from 'react-router-dom'
import { Sheet, Stub } from './ui'

/**
 * Class-based error boundary for catching render errors
 */
class ErrorBoundaryClass extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo })

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    // TODO: Log to error reporting service (e.g., Sentry)
    // logErrorToService(error, errorInfo)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback error={this.state.error} resetError={this.handleReset} />
      )
    }

    return this.props.children
  }
}

ErrorBoundaryClass.propTypes = {
  children: PropTypes.node.isRequired,
}

/**
 * Fallback UI when an error occurs: one sheet on the desk with the
 * heading, the sentence, and the way back.
 */
function ErrorFallback({ error, resetError }) {
  const isDev = process.env.NODE_ENV === 'development'

  return (
    <div className="min-h-screen bg-desk flex items-center justify-center px-4 sm:px-6 lg:px-8 py-pitch-2">
      <Sheet
        title="Something went wrong"
        className="w-full max-w-2xl"
        bodyClassName="pb-pitch"
      >
        <p className="font-sans text-ink-soft leading-pitch mt-pitch max-w-[64ch]">
          We're sorry, but something unexpected happened. Please try again or
          return to the home page.
        </p>

        {isDev && error && (
          <div className="mt-pitch font-mono text-sm leading-pitch text-ink">
            <p className="break-all">{error.message || String(error)}</p>
            {error.stack && (
              <pre className="mt-pitch text-xs leading-pitch text-ink-soft overflow-auto max-h-[240px]">
                {error.stack}
              </pre>
            )}
          </div>
        )}

        <div className="mt-pitch flex flex-wrap items-center gap-x-8 gap-y-pitch">
          {resetError && (
            <Stub type="button" onClick={resetError} className="h-pitch-2">
              Try Again
            </Stub>
          )}

          <Stub as={Link} to="/" variant="word" className="leading-pitch">
            Go Home
          </Stub>
        </div>
      </Sheet>
    </div>
  )
}

ErrorFallback.propTypes = {
  error: PropTypes.object,
  resetError: PropTypes.func,
}

/**
 * Route error boundary for React Router errors
 */
function RouteErrorBoundary() {
  const error = useRouteError()

  return <ErrorFallback error={error} />
}

// Default export is the class component for wrapping children
export default ErrorBoundaryClass

// Named exports for specific use cases
export { ErrorFallback, RouteErrorBoundary }
